import { Injectable } from '@angular/core';
import { take } from 'rxjs/operators';
import { UsersService } from '../../users/shared/users.service';

/*
Бэкенд для пользователей без загруженного фото отдаёт вот эту дефолтную
аватарку. Такой случай трактуем как отсутствие фото, чтобы вместо
заглушки можно было показать инициалы.
 */
const DEFAULT_AVATAR_BASE64 =
  'iVBORw0KGgoAAAANSUhEUgAAADAAAAAwCAYAAABXAvmHAAAACXBIWXMAAAsTAAALEwEAmpwYAAADvElEQVRogdXaS4hcRRQG4G+SyTxQjJIHo0hGJQjGxERExdciogZBISiYhRDUCKJGJbrJwo3oxp26EnfGR1AZsxM1ERGVgCEaNfG1ia/E52hiCJMZEtvFqbY77fRM39vVPeMPl+rue85f/6mqW/fUoXvkRw+W42zMT98P4wA+x98d6DMLlmELfkOlyfU7XsCKXJ32ZOAYwDPYgDk4hp34En8J4fNxAa5I9hUR7L0Yy6ChNBbhoyToADZicAr7wWRzMPnsxuIOa2yKHryVhLyLBQV8F+K95LtdzFzX8VAS8IlYFkUxiM8Sx6aMulpCn1gGx3F+GzzLcEIsv3kZdLWM28XIvZqBa1viWlfGuezauza1W0r61+P51K7OwNUy9opRW5iBayhxfZqBq2Ucwh8Z+Y6IF2BhlF1CfZgo6TsZJtBfxrFsAJWyHTZBb+LsGr5OHZ6VgeuMxLW3jHPZGdif2hxJ2XkNnIVQNoA9qc0RwOUNnIVQNoAPU3tRSf96XJ3anRm4WsbpYuc4qL2UvBejiWt+Bl2FUM1Er2yD4/rE8WZZgnbS2JHU3toGR9V3ZEqrDmGxyCT3K7eMevGzyGgXZdRVCNuVzyTvTr5vZ1VUEDcnEV9hbgG/fnyffG/qgK6W0SP274oY0VaxKfnskaew0BYuE8/CUVzVgv11GE8+l3ZQVyFsFCM6hgdNfkY+BZvFnl/BA11T1yLuFzWhijgrvIzH8UT6fDjdGxdBzkqsEGLH/bcqdwwvYWXODjv1AC0QQs8RtdBvxZHxzw71Vwqn4j58nNqig3EP3hcVjr680qbGUjwlzsX1S2QX1pg+kNX4oMH3JzyGMzsjOUTdiDfEkqiIFGAb7sIXdWJ+xHN4RLyh1+FhPIvv6uz24c7EcSL9NoGt2ksQT8JpYseoHiErIv19EsN1dnOxXsxCs9J6/Uytd3IiOZw4R+vsduMO5cqW+sWeXd36qjXQDaauPsMS3Jb8n07X5vTbkml8B1Mf1bd7Bb+KLbq3VfHDIrepLpNX1E5N3cQ1qe/jamnH0HRO5+KH5PCOKL7ONJarleK/MU0QO5LhVgWmrAuYh9eEttebGa1VS41zFq1yYUDMQAU3TGYwkm6u7aKoorhFaHyx8UafSIePmJ2jX8WA0HhIwxK/RES2YwZEFUX1Ob2Q2stkVWpLVce6jKrGldQCuLjh5mxGVeMq/t8B/HuumCMejDGza+9vhl6h9Zfql6Uitx/FozOnqxCOisLaEJHuTpdBztZrTY/4E0bWc2oXsesfTrAhFiZn1NsAAAAASUVORK5CYII=';

@Injectable()
export class AvatarCacheService {
  public avatarUrls: Record<number, string> = {};
  private loadingAvatarIds = new Set<number>();
  private failedAvatarIds = new Set<number>();

  constructor(
    private usersSvc: UsersService
  ) {}

  /**
   * Является ли пришедший data-URL дефолтной аватаркой бэкенда
   * (т.е. фактически "фото не загружено").
   */
  public isDefaultAvatar(url: string | null | undefined): boolean {
    return !!url && url.includes(DEFAULT_AVATAR_BASE64);
  }

  public getAvatarUrl(userId: number | null, email: string | null): string | null {
    if (!userId || !email) {
      return null;
    }

    if (this.avatarUrls[userId]) {
      return this.avatarUrls[userId];
    }

    if (!this.loadingAvatarIds.has(userId) && !this.failedAvatarIds.has(userId)) {
      this.loadingAvatarIds.add(userId);
      this.usersSvc
        .getProfilePhotoByEmail(email)
        .pipe(take(1))
        .subscribe({
          next: (blob) => {
            if (!blob || blob.size === 0) {
              this.failedAvatarIds.add(userId);
              this.loadingAvatarIds.delete(userId);
              return;
            }
            const reader = new FileReader();
            reader.onloadend = () => {
              const url = reader.result as string;
              if (this.isDefaultAvatar(url)) {
                // Дефолтная картинка с бэка == фото нет: больше не пытаемся грузить.
                this.failedAvatarIds.add(userId);
              } else {
                this.avatarUrls[userId] = url;
              }
              this.loadingAvatarIds.delete(userId);
            };
            reader.readAsDataURL(blob);
          },
          error: () => {
            this.failedAvatarIds.add(userId);
            this.loadingAvatarIds.delete(userId);
          },
        });
    }

    return null;
  }

  public clearCache(): void {
    this.avatarUrls = {};
    this.loadingAvatarIds.clear();
    this.failedAvatarIds.clear();
  }
}
